import type * as React from 'react';
import {
  useWatch,
  type Control,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';
import { Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';

import { lineFieldPath } from './adjustment-line-paths';
import {
  adjustmentTypesFor,
  DEMURRAGE_TYPE,
  lineTotal,
  type AdjustmentNoteSubmitValues,
} from './adjustment-note-schema';

type NoteType = 'Credit' | 'Debit';

interface RowProps {
  readonly noteType: NoteType;
  readonly index: number;
  readonly control: Control<AdjustmentNoteSubmitValues>;
  readonly register: UseFormRegister<AdjustmentNoteSubmitValues>;
  readonly setValue: UseFormSetValue<AdjustmentNoteSubmitValues>;
  readonly disabled: boolean;
  readonly removable: boolean;
  readonly onRemove: () => void;
}

function testidKind(noteType: NoteType): string {
  return noteType === 'Credit' ? 'credit' : 'debit';
}

function lineId(kind: string, index: number, field: string): string {
  return `${kind}-line-${String(index)}-${field}`;
}

// A sell-side line maps Launchpad's Type / Amount / Description onto our
// adjustment-line shape: a flat Amount is the line's unit_price (quantity 1),
// while a Debit "Demurrage" line is Days (quantity) × Rate/Day (unit_price).
export function SellNoteLineRow(props: RowProps) {
  const kind = testidKind(props.noteType);
  const type = useWatch({ control: props.control, name: lineFieldPath(props.index, 'type') });
  const isDemurrage = props.noteType === 'Debit' && type === DEMURRAGE_TYPE;
  return (
    <li
      className="border-border bg-muted/30 flex flex-col gap-2 rounded-md border p-3"
      data-testid={`${kind}-line-${String(props.index)}`}
    >
      <RowHeader {...props} kind={kind} />
      <TypeCell {...props} kind={kind} />
      {isDemurrage ? (
        <DemurrageCells {...props} kind={kind} />
      ) : (
        <AmountCell {...props} kind={kind} />
      )}
      <DescriptionCell {...props} kind={kind} />
    </li>
  );
}

interface CellProps extends RowProps {
  readonly kind: string;
}

function RowHeader(props: CellProps) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground text-xs font-medium">
        Line {String(props.index + 1)}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Remove line ${String(props.index + 1)}`}
        data-testid={`${props.kind}-line-${String(props.index)}-remove`}
        disabled={props.disabled || !props.removable}
        onClick={props.onRemove}
      >
        <Trash2 className="size-4" />
      </Button>
    </div>
  );
}

function TypeCell(props: CellProps) {
  const id = lineId(props.kind, props.index, 'type');
  const reg = props.register(lineFieldPath(props.index, 'type'), {
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (props.noteType !== 'Debit') return;
      const demurrage = e.target.value === DEMURRAGE_TYPE;
      props.setValue(lineFieldPath(props.index, 'unit'), demurrage ? 'days' : 'each');
      if (!demurrage) props.setValue(lineFieldPath(props.index, 'quantity'), 1);
    },
  });
  return (
    <div>
      <Label htmlFor={id}>Type</Label>
      <Select id={id} disabled={props.disabled} {...reg}>
        <option value="">Select type…</option>
        {adjustmentTypesFor(props.noteType).map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </Select>
    </div>
  );
}

function AmountCell(props: CellProps) {
  const id = lineId(props.kind, props.index, 'amount');
  return (
    <div>
      <Label htmlFor={id}>Amount</Label>
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0"
        disabled={props.disabled}
        {...props.register(lineFieldPath(props.index, 'unit_price'), { valueAsNumber: true })}
      />
    </div>
  );
}

function DemurrageCells(props: CellProps) {
  const days = useWatch({ control: props.control, name: lineFieldPath(props.index, 'quantity') });
  const rate = useWatch({ control: props.control, name: lineFieldPath(props.index, 'unit_price') });
  const daysId = lineId(props.kind, props.index, 'days');
  const rateId = lineId(props.kind, props.index, 'rate');
  const totalId = lineId(props.kind, props.index, 'line_total');
  return (
    <div className="grid grid-cols-3 gap-2">
      <div>
        <Label htmlFor={daysId}>Days</Label>
        <Input
          id={daysId}
          type="number"
          inputMode="numeric"
          step="1"
          min="0"
          disabled={props.disabled}
          {...props.register(lineFieldPath(props.index, 'quantity'), { valueAsNumber: true })}
        />
      </div>
      <div>
        <Label htmlFor={rateId}>Rate/Day</Label>
        <Input
          id={rateId}
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          disabled={props.disabled}
          {...props.register(lineFieldPath(props.index, 'unit_price'), { valueAsNumber: true })}
        />
      </div>
      <div>
        <Label htmlFor={totalId}>Total</Label>
        <output
          id={totalId}
          data-testid={totalId}
          className="border-input bg-muted text-foreground flex h-9 items-center rounded-md border px-3 text-sm font-medium"
        >
          {lineTotal(days, rate).toFixed(2)}
        </output>
      </div>
    </div>
  );
}

function DescriptionCell(props: CellProps) {
  const id = lineId(props.kind, props.index, 'description');
  return (
    <div>
      <Label htmlFor={id}>Description</Label>
      <Input
        id={id}
        type="text"
        disabled={props.disabled}
        {...props.register(lineFieldPath(props.index, 'description'))}
      />
    </div>
  );
}
